import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { Section } from "@/components/layout/Sections";
import api, { mediaUrl } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";

const BRAND_LABELS = { ktm: "KTM", husqvarna: "Husqvarna", gasgas: "GasGas Trial" };

export default function NewsDetail() {
  const { id } = useParams();
  const location = useLocation();
  const [news, setNews] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/news/${id}`).then((r) => setNews(r.data)).catch(() => setErr("News non trovata"));
  }, [id]);

  // Detect if we came from a brand page (/moto/:brand/news/:id) or Racing Team
  const match = location.pathname.match(/^\/moto\/([a-z]+)\/news\//);
  const fromBrand = match && BRAND_LABELS[match[1]] ? match[1] : null;
  const backTo = fromBrand ? `/moto/${fromBrand}` : "/moto/racing-team";
  const backLabel = fromBrand ? `Torna a ${BRAND_LABELS[fromBrand]}` : "Torna al Racing Team";

  return (
    <PageLayout>
      <Section>
        <Link to={backTo} className="label-eyebrow inline-flex items-center gap-2 text-gray-500 hover:text-gray-900" data-testid="news-back">
          <ArrowLeft size={14} /> {backLabel}
        </Link>
        {err && <div className="mt-10 text-red-600" data-testid="news-error">{err}</div>}
        {news && (
          <article className="mt-8 max-w-3xl">
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">
              {new Date(news.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
            </span>
            <h1 className="font-display uppercase text-4xl md:text-5xl leading-[0.95] mt-3" data-testid="news-title">
              {news.title}
            </h1>
            {news.cover_image && (
              <figure className="mt-8" data-testid="news-cover">
                <img
                  src={mediaUrl(news.cover_image)}
                  alt={news.title}
                  className="w-full h-auto max-h-[70vh] object-contain bg-gray-100"
                  loading="lazy"
                />
              </figure>
            )}
            <p className="text-xl text-gray-700 leading-relaxed mt-8 font-body">{news.excerpt || (news.content || news.body || "").split(/\n\n+/)[0]?.slice(0, 280)}</p>
            <div className="mt-8 font-body text-gray-800 leading-relaxed news-content" data-testid="news-body">
              <ReactMarkdown
                components={{
                  p: (props) => <p className="mb-5 text-lg" {...props} />,
                  strong: (props) => <strong className="text-gray-900 font-semibold" {...props} />,
                  blockquote: (props) => (
                    <blockquote className="my-6 border-l-4 border-[color:var(--nsa-orange)] bg-gray-50 px-6 py-4 italic text-gray-700" {...props} />
                  ),
                  img: ({ src, alt }) => (
                    <figure className="my-8">
                      <img src={mediaUrl(src)} alt={alt} className="w-full h-auto" />
                      {alt && <figcaption className="mt-2 text-sm text-gray-500 italic">{alt}</figcaption>}
                    </figure>
                  ),
                  h2: (props) => <h2 className="font-display uppercase text-2xl md:text-3xl mt-10 mb-4" {...props} />,
                  h3: (props) => <h3 className="font-display uppercase text-xl md:text-2xl mt-8 mb-3" {...props} />,
                }}
              >
                {news.body || news.content || ""}
              </ReactMarkdown>
            </div>
          </article>
        )}
      </Section>
    </PageLayout>
  );
}
